import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { API_BASE_URL } from '@/utils/api'
import { i18n } from '@/i18n'

const STORAGE_KEY = 'auth_session'
const LOCALE_KEY = 'app_locale'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const loading = ref(false)

  // Getters
  const isAuthenticated = computed(() => user.value !== null)
  
  // Función para verificar permisos
  function hasPermission(permission) {
    if (!user.value || !user.value.permissions) {
      return false
    }
    return user.value.permissions.includes(permission)
  }
  
  // Función para verificar múltiples permisos (OR)
  function hasAnyPermission(permissions) {
    if (!Array.isArray(permissions)) {
      return hasPermission(permissions)
    }
    return permissions.some(permission => hasPermission(permission))
  }
  
  /**
   * Los roles pueden llegar como strings o como objetos {name}, según el
   * endpoint. Antes había dos ramas para cubrirlo, pero las dos preguntaban
   * `Array.isArray(roles)`, así que la segunda no se ejecutaba nunca: con
   * objetos, hasRole('admin') siempre devolvía false.
   */
  function hasRole(roleName) {
    const roles = user.value?.roles
    if (!Array.isArray(roles)) return false
    return roles.some((role) => (typeof role === 'string' ? role : role?.name) === roleName)
  }

  // Actions
  function loadUserFromStorage() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const sessionData = JSON.parse(stored)
        user.value = sessionData.user
        return true
      }
    } catch (error) {
      console.error('Error loading user from storage:', error)
      localStorage.removeItem(STORAGE_KEY)
    }
    user.value = null
    return false
  }

  function saveSession(userData) {
    try {
      const sessionData = {
        user: userData,
        timestamp: Date.now(),
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData))
      if (userData?.locale) {
        localStorage.setItem(LOCALE_KEY, userData.locale)
      }
      user.value = userData
    } catch (error) {
      console.error('Error saving session:', error)
    }
  }

  function clearSession() {
    try {
      localStorage.removeItem(STORAGE_KEY)
      user.value = null
    } catch (error) {
      console.error('Error clearing session:', error)
    }
  }

  function getPreferredLocale() {
    return user.value?.locale || localStorage.getItem(LOCALE_KEY) || 'es'
  }

  async function login(email, password, remember = false) {
    loading.value = true
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, remember }),
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          errors: data.errors || [],
          message: data.message || i18n.global.t('auth.err_login_failed'),
        }
      }

      if (data.data) {
        // Guardar sesión en localStorage y en el store
        saveSession(data.data)
        return { success: true, user: data.data }
      }

      return { success: false, message: i18n.global.t('auth.err_unknown') }
    } catch (error) {
      console.error('Error en login:', error)
      return {
        success: false,
        message: i18n.global.t('auth.err_connection'),
      }
    } finally {
      loading.value = false
    }
  }

  async function register(name, email, password, password_confirmation) {
    loading.value = true
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name, email, password, password_confirmation }),
      })

      const data = await response.json()

      if (!response.ok) {
        return {
          success: false,
          errors: data.errors || [],
          message: data.message || i18n.global.t('auth.err_register_failed'),
        }
      }

      // No guardar sesión: el usuario debe verificar el correo antes de poder iniciar sesión
      if (data.data) {
        return { success: true, message: data.message || i18n.global.t('auth.register_check_inbox') }
      }

      return { success: false, message: i18n.global.t('auth.err_unknown') }
    } catch (error) {
      console.error('Error en registro:', error)
      return {
        success: false,
        message: i18n.global.t('auth.err_connection'),
      }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    loading.value = true
    try {
      await fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include',
      })
      // Limpiar sesión local
      clearSession()
      return true
    } catch (error) {
      console.error('Error en logout:', error)
      // Limpiar sesión local incluso si hay error
      clearSession()
      return false
    } finally {
      loading.value = false
    }
  }

  function updateUser(userData) {
    saveSession(userData)
  }

  /**
   * Obtiene el usuario actual desde la API (/me) y lo guarda en el store.
   * Útil cuando el usuario llega con sesión de cookie (p. ej. tras verificar email).
   * Retorna true si se obtuvo el usuario, false en caso contrario.
   */
  async function fetchUserFromApi() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'include',
      })
      if (!response.ok) return false
      const data = await response.json()
      if (data.data) {
        saveSession(data.data)
        return true
      }
    } catch (error) {
      console.error('Error fetching user from API:', error)
    }
    return false
  }

  /**
   * Verifica la sesión con el backend
   * Si la sesión es inválida, limpia el estado local
   * Retorna true si la sesión es válida, false si no lo es
   */
  async function checkSession() {
    // Si no hay usuario en localStorage, no hay nada que verificar
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      user.value = null
      return false
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/me`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'include',
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.data) {
          // Actualizar usuario con datos del backend
          saveSession(data.data)
          return true
        }
      }
      
      // Si la respuesta no es OK (401, etc.), la sesión es inválida
      if (response.status === 401) {
        clearSession()
        return false
      }
      
      // Para otros errores, mantener el usuario local pero retornar false
      return false
    } catch (error) {
      console.error('Error verificando sesión:', error)
      // En caso de error de conexión, mantener el usuario local
      // pero retornar false para indicar que necesita verificación
      return false
    }
  }

  // Inicializar usuario desde storage al crear el store
  loadUserFromStorage()

  return {
    // State
    user,
    loading,
    // Getters
    isAuthenticated,
    // Actions
    loadUserFromStorage,
    login,
    register,
    logout,
    updateUser,
    clearSession,
    checkSession,
    fetchUserFromApi,
    getPreferredLocale,
    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasRole,
  }
})

